import { apiGet } from '../../../adapters/api-client/api';
import { HoldbackView } from './cycle-progress';

export interface EarningsBuckets {
  releasedToDate: number;
  pending: number;
  holdbackInProgress: number;
  disputed: number;
}

export interface EnrollmentEarnings extends EarningsBuckets {
  enrollmentId: string;
  /** What every amount on this enrollment is denominated in. */
  currency: string;
  courseId: string;
  state: string;
  bookingFee: { amount: number; released: boolean };
  holdback: HoldbackView;
}

export interface TeacherEarnings {
  teacherId: string;
  /** What the totals are denominated in. */
  currency: string;
  totals: EarningsBuckets;
  enrollments: EnrollmentEarnings[];
}

export interface MilestoneEarningRow {
  sequence: number;
  type: 'session' | 'final_project';
  status: string;
  amount: number;
  releaseId?: string;
  releasedAt?: string;
  paymentMethod?: string;
  /** MC-03: the teacher did not show; refunded to the family, never paid or delivered. */
  notDelivered?: boolean;
  /** When the family's refund completed; absent while it is in process. */
  refundedAt?: string;
}

export function getEarnings(): Promise<{ earnings: TeacherEarnings }> {
  return apiGet('/api/v1/teachers/me/earnings');
}

export function getEarningsMilestones(
  enrollmentId: string,
): Promise<{ enrollmentId: string; milestones: MilestoneEarningRow[] }> {
  const params = new URLSearchParams({ enrollmentId });
  return apiGet(`/api/v1/teachers/me/earnings/milestones?${params.toString()}`);
}

/**
 * One dated money movement. Negative for a refund. FAMILY_REFUND (MC-03) is a
 * teacher no-show returned to the family from escrow: shown, never counted
 * against the teacher, who was not paid for it.
 */
export interface LedgerEntry {
  at: string;
  kind: 'RELEASE' | 'REFUND' | 'FAMILY_REFUND';
  enrollmentId: string;
  courseId: string;
  sequence?: number;
  milestoneType?: string;
  amount: number;
  reason?: string;
  reference?: string;
  paymentMethod?: string;
}

/**
 * Money the platform holds that the ledger cannot place on a date.
 *
 * Shown rather than dropped: a history that silently omits what it cannot
 * date stops agreeing with settlement, and nothing on screen explains why.
 */
export interface UnplacedMoney {
  undatedReleases: { count: number; amount: number; reason: string };
  /** Refunds issued before per-refund records existed. Shrinks to zero on its own. */
  refundsWithoutRecord: { amount: number; reason: string };
}

export interface TeacherLedger {
  teacherId: string;
  currency: string;
  period: { from: string; to: string } | null;
  entries: LedgerEntry[];
  totals: { released: number; refunded: number; refundedAllTime: number; net: number };
  unplaced: UnplacedMoney;
}

export function getLedger(period?: { from: string; to: string }): Promise<{
  ledger: TeacherLedger;
}> {
  const query = period ? `?${new URLSearchParams(period).toString()}` : '';
  return apiGet(`/api/v1/teachers/me/ledger${query}`);
}

/**
 * How the teacher gets paid (TCH-16-FR-09).
 *
 * Every number comes from the API, which reads the constants the escrow
 * actually applies. Nothing here is restated on the client: a rate written
 * twice is a rate that will eventually disagree with itself.
 */
export interface PaymentTerms {
  platformCommissionRate: number;
  bookingFeeRate: number;
  sessionPoolRate: number;
  sessionCount: number;
  holdbackRate: number;
  holdbackHours: number;
  cancellationCutoffHours: number;
  lateCancellationRate: number;
}

export function getPaymentTerms(): Promise<{ terms: PaymentTerms }> {
  return apiGet('/api/v1/teachers/me/payment-terms');
}
